import { useEffect } from 'react';

/**
 * How much the signed-in shell is scaled down, as `--ui-scale` on the document root.
 *
 * <h2>Why a zoom at all</h2>
 *
 * The shell is drawn in the design's own pixel sizes — a 34px control, a 14px breadcrumb —
 * and the design then renders the whole of it slightly compact: **0.85 on a laptop, 0.90 on
 * a large monitor**, never outside that range, so the smallest text never drops below about
 * 11px. `the previous product's AppNav.tsx` sets it and applies it with CSS `zoom`.
 *
 * <p>It was left out of this application's shell on 8 October 2026 as a hack worth skipping,
 * on the reasoning that the explicit px values were already small. They are the UNSCALED
 * ones: the top bar came out about 9% larger than the design's everywhere, which is what
 * "the text is small on the other one" was.
 *
 * <h2>`zoom`, not `transform: scale()`</h2>
 *
 * `zoom` scales the layout — the box genuinely becomes smaller and its neighbours move up to
 * it. `transform` scales the painting and leaves the original box reserving its original
 * space, so a scaled top bar would still be 64px tall with its contents shrunk inside it,
 * which is the opposite of what this is for.
 *
 * <h2>It is set on the root and applied by whoever wants it</h2>
 *
 * A variable rather than a wrapper, because the rail is deliberately **not** scaled: its
 * lengths come from Tailwind's spacing scale, which is already relative to this
 * application's 14px root font, so zooming it as well would compact it twice. The top bar's
 * content is in absolute pixels and is the one part of the shell that takes it.
 */
export function useUiScale(): void {
  useEffect(() => {
    const root = document.documentElement;

    const applyScale = () => {
      const width = window.innerWidth;
      /*
       * Below 1024 the rail is hidden and the bar is already narrow, so the design does not
       * compact it further. Above that it ramps with the window and is clamped at both ends:
       * 0.85 up to about 1700px, reaching 0.90 at 1800 and staying there.
       */
      const scale = width < 1024 ? 0.9 : Math.min(0.9, Math.max(0.85, width / 2000));
      root.style.setProperty('--ui-scale', scale.toFixed(4));
    };

    applyScale();
    window.addEventListener('resize', applyScale);

    return () => {
      window.removeEventListener('resize', applyScale);
      // Removed rather than left at its last value: the variable belongs to the signed-in
      // shell, and an auth screen reading a stale 0.85 would be scaled by a layout it is
      // not inside.
      root.style.removeProperty('--ui-scale');
    };
  }, []);
}
